import {
  MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import maplibregl, {
  LngLatBoundsLike,
  Map as MapLibreInstance,
  Marker,
} from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import bbox from '@turf/bbox'
import { featureCollection, point } from '@turf/helpers'
import { renderToStaticMarkup } from 'react-dom/server'
import { useTrackerRuntime } from '../runtime'
import {
  fetchMapTileManifest,
  normalizeCountry,
  resolveMapTiles,
} from '../mapTiles'
import {
  collapseMapAttribution,
  createPmtilesMapStyle,
  ensurePmtilesProtocol,
  MAP_SOURCE_NAME,
} from '../mapStyle'
import { OrderDataInterface } from '../types'
import CarMarker from './Icons/CarIconMarker'
import HomeIconMarker from './Icons/HomeIconMarker'
import MarkerIconMerchant from './Icons/MerchantIcon'

const MAP_FIT_PADDING = 75
const DRIVER_ANIMATION_DURATION_MS = 1400

type MarkerKind = 'driver' | 'dropoff' | 'pickup'
type MarkerCoordinates = [number, number]

const toBoundsLike = (box: number[]): LngLatBoundsLike | null =>
  box.length === 4
    ? [
        [box[0], box[1]],
        [box[2], box[3]],
      ]
    : null

const isCoordinate = (value: unknown): boolean => {
  if (typeof value === 'number') {
    return Number.isFinite(value)
  }

  return (
    typeof value === 'string' &&
    value.trim() !== '' &&
    Number.isFinite(Number(value))
  )
}

interface MapLibreMapProps {
  data: OrderDataInterface
}

type MapErrorState =
  | 'country-unavailable'
  | 'manifest-unavailable'
  | 'map-unavailable'

const MapLibreMap = ({ data }: MapLibreMapProps) => {
  const runtime = useTrackerRuntime()
  const mapContainerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<MapLibreInstance | null>(null)
  const markersRef = useRef<Partial<Record<MarkerKind, Marker>>>({})
  const driverAnimationFrameRef = useRef<number | null>(null)
  const [archiveUrl, setArchiveUrl] = useState<string | null>(null)
  const [resolvedCountry, setResolvedCountry] = useState<
    string | null
  >(null)
  const [errorState, setErrorState] = useState<MapErrorState | null>(
    null
  )
  const [retryKey, setRetryKey] = useState(0)

  const merchantLatitude = data?.merchant.location.coordinates[1]
  const merchantLongitude = data?.merchant.location.coordinates[0]
  const runnerLatitude = data?.runner?.location?.coordinates?.[1]
  const runnerLongitude = data?.runner?.location?.coordinates?.[0]
  const customerLatitude = data?.customer?.location?.coordinates?.[1]
  const customerLongitude = data?.customer?.location?.coordinates?.[0]
  const status = data.fulfillment?.status
  const country = data.operator?.country
  const mapTilesBaseUrl = runtime.mapTilesBaseUrl

  const finalBbox = useMemo(() => {
    const mapObj: {
      origin: { latitude: number; longitude: number }
      destination: { latitude: number; longitude: number }
      directions: Array<{ latitude: number; longitude: number }>
      runner?: { latitude: number; longitude: number }
    } = {
      origin: {
        latitude: Number(merchantLatitude),
        longitude: Number(merchantLongitude),
      },
      destination: {
        latitude: Number(customerLatitude),
        longitude: Number(customerLongitude),
      },
      directions: [],
    }

    if (
      isCoordinate(runnerLatitude) &&
      isCoordinate(runnerLongitude)
    ) {
      mapObj.runner = {
        latitude: Number(runnerLatitude),
        longitude: Number(runnerLongitude),
      }
    }

    return buildBoundingBox(status, mapObj)
  }, [
    customerLatitude,
    customerLongitude,
    merchantLatitude,
    merchantLongitude,
    runnerLatitude,
    runnerLongitude,
    status,
  ])

  const showCompletedPhoto =
    !!data.order?.dropoff_photo && status === 'completed'
  const latestBboxRef = useRef<number[]>(finalBbox)
  latestBboxRef.current = finalBbox
  const hasFittedRef = useRef(false)

  useEffect(() => {
    if (!errorState || !data.operator?.customer_support_tracking) {
      return undefined
    }

    const atlasWindow = window as Window & {
      Atlas?: { identify: (payload: unknown) => void }
    }

    const timeout = window.setTimeout(() => {
      atlasWindow.Atlas?.identify({
        userId: data.order?.id || data.customer?.address,
        customFields: {
          map_country: resolvedCountry || normalizeCountry(country),
          map_error: errorState,
          map_provider: 'maplibre',
        },
      })
    }, 0)

    return () => window.clearTimeout(timeout)
  }, [
    country,
    data.customer?.address,
    data.operator?.customer_support_tracking,
    data.order?.id,
    errorState,
    resolvedCountry,
  ])

  useEffect(() => {
    let active = true
    let countryResolutionFailed = false
    setArchiveUrl(null)
    setResolvedCountry(null)
    setErrorState(null)

    if (showCompletedPhoto) {
      return () => {
        active = false
      }
    }

    if (!normalizeCountry(country)) {
      setErrorState('country-unavailable')
      return () => {
        active = false
      }
    }

    if (!mapTilesBaseUrl) {
      setErrorState('manifest-unavailable')
      return () => {
        active = false
      }
    }

    fetchMapTileManifest(mapTilesBaseUrl)
      .then(manifest => {
        try {
          return resolveMapTiles(manifest, country)
        } catch (error) {
          countryResolutionFailed = true
          if (active) {
            setErrorState('country-unavailable')
          }
          throw error
        }
      })
      .then(result => {
        if (active) {
          setArchiveUrl(result.archiveUrl)
          setResolvedCountry(result.country)
        }
      })
      .catch(() => {
        if (active && !countryResolutionFailed) {
          setErrorState('manifest-unavailable')
        }
      })

    return () => {
      active = false
    }
  }, [country, mapTilesBaseUrl, retryKey, showCompletedPhoto])

  useEffect(() => {
    if (
      showCompletedPhoto ||
      !archiveUrl ||
      !mapTilesBaseUrl ||
      !mapContainerRef.current ||
      mapRef.current
    ) {
      return
    }

    ensurePmtilesProtocol()
    const initialBounds = toBoundsLike(latestBboxRef.current)
    const map = new maplibregl.Map({
      attributionControl: { compact: true },
      container: mapContainerRef.current,
      style: createPmtilesMapStyle(mapTilesBaseUrl, archiveUrl),
      ...(initialBounds
        ? {
            bounds: initialBounds,
            fitBoundsOptions: { padding: MAP_FIT_PADDING },
          }
        : {}),
    })

    let sourceErrorCount = 0
    const handleMapError = (event: { sourceId?: string }) => {
      if (event.sourceId !== MAP_SOURCE_NAME) {
        return
      }

      sourceErrorCount += 1
      if (sourceErrorCount >= 2) {
        setErrorState('map-unavailable')
      }
    }
    map.on('error', handleMapError)
    map.on('load', () => {
      collapseMapAttribution(map.getContainer())
      map.resize()
    })
    hasFittedRef.current = false
    mapRef.current = map

    return () => {
      map.off('error', handleMapError)
      if (driverAnimationFrameRef.current !== null) {
        window.cancelAnimationFrame(driverAnimationFrameRef.current)
        driverAnimationFrameRef.current = null
      }
      Object.values(markersRef.current).forEach(marker =>
        marker?.remove()
      )
      markersRef.current = {}
      map.remove()
      mapRef.current = null
    }
  }, [archiveUrl, errorState, mapTilesBaseUrl, showCompletedPhoto])

  useEffect(() => {
    if (showCompletedPhoto || !mapRef.current || errorState) {
      return
    }

    const map = mapRef.current
    const updateMap = () => {
      const upsertMarker = (
        kind: MarkerKind,
        coordinates: MarkerCoordinates,
        jsx: JSX.Element
      ): Marker => {
        const existingMarker = markersRef.current[kind]
        if (existingMarker) {
          return existingMarker
        }

        const marker = new maplibregl.Marker({
          anchor: kind === 'driver' ? 'center' : 'bottom',
          element: createMarkerElement(kind, jsx),
        })
          .setLngLat(coordinates)
          .addTo(map)
        markersRef.current[kind] = marker
        return marker
      }

      upsertMarker(
        'pickup',
        [Number(merchantLongitude), Number(merchantLatitude)],
        <MarkerIconMerchant width="55" height="55" />
      )
      upsertMarker(
        'dropoff',
        [Number(customerLongitude), Number(customerLatitude)],
        <HomeIconMarker />
      )

      if (
        !['completed', 'cancelled'].includes(status) &&
        isCoordinate(runnerLatitude) &&
        isCoordinate(runnerLongitude)
      ) {
        const driverMarker = upsertMarker(
          'driver',
          [Number(runnerLongitude), Number(runnerLatitude)],
          <CarMarker
            width="32"
            height="32"
            color="#2563eb"
            status="driver"
          />
        )
        animateMarker(
          driverMarker,
          [Number(runnerLongitude), Number(runnerLatitude)],
          driverAnimationFrameRef
        )
      } else {
        markersRef.current.driver?.remove()
        delete markersRef.current.driver
        if (driverAnimationFrameRef.current !== null) {
          window.cancelAnimationFrame(driverAnimationFrameRef.current)
          driverAnimationFrameRef.current = null
        }
      }

      const boundsLike = toBoundsLike(finalBbox)
      if (boundsLike) {
        map.fitBounds(boundsLike, {
          padding: MAP_FIT_PADDING,
          animate: hasFittedRef.current,
        })
        hasFittedRef.current = true
      }

      map.resize()
    }

    if (map.isStyleLoaded()) {
      updateMap()
      return
    }

    map.once('load', updateMap)
    return () => {
      map.off('load', updateMap)
    }
  }, [
    archiveUrl,
    customerLatitude,
    customerLongitude,
    errorState,
    finalBbox,
    merchantLatitude,
    merchantLongitude,
    runnerLatitude,
    runnerLongitude,
    showCompletedPhoto,
    status,
  ])

  if (showCompletedPhoto) {
    return (
      <img src={data.order?.dropoff_photo} alt="Delivery Completed" />
    )
  }

  if (errorState) {
    return (
      <div
        data-map-provider="maplibre"
        data-map-country={resolvedCountry || ''}
        data-map-error={errorState}
        className="flex h-full w-full flex-col items-center justify-center gap-3 bg-gray-100 p-6 text-center text-gray-700"
      >
        <p>We couldn&apos;t load the delivery map.</p>
        <a
          href={googleMapsUrl(customerLatitude, customerLongitude)}
          target="_blank"
          rel="noreferrer"
          className="font-semibold underline"
        >
          Open destination in Google Maps
        </a>
        <button
          type="button"
          className="text-sm underline"
          onClick={() => setRetryKey(value => value + 1)}
        >
          Try map again
        </button>
      </div>
    )
  }

  return (
    <div
      ref={mapContainerRef}
      data-map-provider="maplibre"
      data-map-country={resolvedCountry || ''}
      data-map-error=""
      style={{
        width: '100%',
        height: '100%',
        isolation: 'isolate',
        position: 'relative',
        zIndex: 0,
      }}
    />
  )
}

function googleMapsUrl(
  latitude: unknown,
  longitude: unknown
): string {
  const lat = Number(latitude)
  const lng = Number(longitude)
  const query =
    Number.isFinite(lat) && Number.isFinite(lng)
      ? `${lat},${lng}`
      : ''

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    query
  )}`
}

function createMarkerElement(
  kind: MarkerKind,
  jsx: JSX.Element
): HTMLElement {
  const element = document.createElement('div')
  const isDriver = kind === 'driver'
  const color =
    kind === 'pickup'
      ? '#f59e0b'
      : kind === 'dropoff'
      ? '#16a34a'
      : '#2563eb'
  const size = isDriver ? 46 : 40

  element.dataset.markerKind = kind
  element.setAttribute(
    'aria-label',
    kind === 'pickup'
      ? 'Pickup location'
      : kind === 'dropoff'
      ? 'Dropoff location'
      : 'Driver location'
  )
  element.style.width = `${size}px`
  element.style.height = isDriver ? `${size}px` : '48px'
  element.style.zIndex = isDriver ? '30' : '10'

  const badge = document.createElement('div')
  badge.style.alignItems = 'center'
  badge.style.background = color
  badge.style.border = '3px solid white'
  badge.style.borderRadius = '9999px'
  badge.style.boxShadow =
    '0 4px 10px rgba(15, 23, 42, 0.28), 0 1px 3px rgba(15, 23, 42, 0.2)'
  badge.style.display = 'flex'
  badge.style.height = `${size}px`
  badge.style.justifyContent = 'center'
  badge.style.position = 'relative'
  badge.style.width = `${size}px`
  badge.style.zIndex = '2'
  badge.innerHTML = renderToStaticMarkup(jsx)

  const icon = badge.firstElementChild as HTMLElement | null
  if (icon) {
    icon.style.backgroundColor = 'transparent'
    icon.style.position = 'relative'
    icon.style.zIndex = '2'
  }
  element.appendChild(badge)

  if (!isDriver) {
    const pointer = document.createElement('div')
    pointer.style.background = color
    pointer.style.borderBottom = '2px solid white'
    pointer.style.borderRight = '2px solid white'
    pointer.style.bottom = '3px'
    pointer.style.height = '12px'
    pointer.style.left = '50%'
    pointer.style.position = 'absolute'
    pointer.style.transform = 'translateX(-50%) rotate(45deg)'
    pointer.style.width = '12px'
    pointer.style.zIndex = '1'
    element.appendChild(pointer)
  }

  return element
}

function animateMarker(
  marker: Marker,
  destination: MarkerCoordinates,
  animationFrameRef: MutableRefObject<number | null>
): void {
  if (animationFrameRef.current !== null) {
    window.cancelAnimationFrame(animationFrameRef.current)
  }

  const start = marker.getLngLat()
  if (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    (start.lng === destination[0] && start.lat === destination[1])
  ) {
    marker.setLngLat(destination)
    animationFrameRef.current = null
    return
  }

  const startedAt = performance.now()
  const move = (now: number): void => {
    const progress = Math.min(
      (now - startedAt) / DRIVER_ANIMATION_DURATION_MS,
      1
    )
    const easedProgress =
      progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2

    marker.setLngLat([
      start.lng + (destination[0] - start.lng) * easedProgress,
      start.lat + (destination[1] - start.lat) * easedProgress,
    ])

    if (progress < 1) {
      animationFrameRef.current = window.requestAnimationFrame(move)
      return
    }

    animationFrameRef.current = null
  }

  animationFrameRef.current = window.requestAnimationFrame(move)
}

function buildBoundingBox(
  status: string,
  directions: {
    runner?: { latitude: number; longitude: number }
    directions: { latitude: number; longitude: number }[]
    origin: { latitude: number; longitude: number }
    destination: { latitude: number; longitude: number }
  }
): number[] {
  const bboxPoints = []

  try {
    if (
      isCoordinate(directions.origin.longitude) &&
      isCoordinate(directions.origin.latitude)
    ) {
      bboxPoints.push(
        point([
          directions.origin.longitude,
          directions.origin.latitude,
        ])
      )
    }

    if (
      isCoordinate(directions.destination.longitude) &&
      isCoordinate(directions.destination.latitude)
    ) {
      bboxPoints.push(
        point([
          directions.destination.longitude,
          directions.destination.latitude,
        ])
      )
    }

    if (status === 'in_progress' && directions.runner) {
      bboxPoints.push(
        point([
          directions.runner.longitude,
          directions.runner.latitude,
        ])
      )
    }

    return bbox(featureCollection(bboxPoints))
  } catch (_error) {
    return []
  }
}

export default MapLibreMap
